import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import { InputSearchbox } from "components/Form/InputSearchbox";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import moment from "moment";
import {
  getAllRaffleCities,
  getAllRaffleWinners,
  setModalFormOpen,
} from "redux/actions";
import { IconPlusRounded } from "@aksara-ui/icons";
import { AddWinnerModal } from "./components";
import { stringToColor } from "utilities/helper/stringToColor";
import FilterKit from "components/FilterKit";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const RaffleWinnersContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading, isModalFormOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { raffleWinners } = useSelector(
    (state: any) => state.raffleWinnerReducer
  );
  const { currentPage, totalPage, limitPage, lengthPage, filterCityPage } =
    useSelector((state: any) => state.raffleWinnerReducer);
  const { citiesFilter } = useSelector((state: any) => state.raffleReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllRaffleWinners(
        1,
        event.target.value,
        limitPage,
        filterCityPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllRaffleWinners(currentPage, "", limitPage, filterCityPage) as any
    );
  };

  const handleOpenFormModal = () => {
    dispatch(setModalFormOpen(true) as any);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
  };

  useEffect(() => {
    dispatch(getAllRaffleCities() as any);
    dispatch(getAllRaffleWinners() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Raffle Winner List
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"space-between"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="search"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />

            <FilterKit
              orderByOptions={[
                {
                  key: citiesFilter,
                  label: "City",
                  options: citiesFilter.map((city: any) => {
                    return {
                      label: city.city,
                      value: city.id ?? "",
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllRaffleWinners(
                    1,
                    "",
                    limitPage,
                    e[citiesFilter] ? e[citiesFilter].value : ""
                  ) as any
                );
              }}
            />
          </Box>

          <Stack
            display={"flex"}
            justifyContent={"right"}
            flexDirection={"row"}
            style={{ gap: isMobile ? 10 : 20 }}
          >
            <Button
              variant="primary"
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Add Winner
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell maxWidth={3}></TableHeadCell>
              <TableHeadCell>Milk ID</TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Raffle Code</TableHeadCell>
              <TableHeadCell>Date/Time</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={5} />
            ) : !raffleWinners.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={5} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              raffleWinners.map((winner: any, index: any) => {
                const name = winner.raffle.user ? winner.raffle.user.name : "-";
                const color = stringToColor(name);

                return (
                  <TableBodyRow key={winner.id}>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="success"
                        style={{
                          borderRadius: "100%",
                          padding: 10,
                        }}
                      >
                        {10 * (currentPage - 1) + (index + 1)}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      {winner.raffle.user ? winner.raffle.user.milk_id : "-"}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Avatar
                        name={name}
                        backgroundColor={color}
                        style={{ marginRight: 15 }}
                      />
                      {name}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge
                        size="lg"
                        variant="default"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {winner.giveaway_code.toLocaleString()}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      {moment(winner.created_at).format(
                        "DD MMMM YYYY, HH:mm:ss"
                      )}
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllRaffleWinners(
                select,
                searchValue,
                limitPage,
                filterCityPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllRaffleWinners(
                  1,
                  searchValue,
                  selectedItem,
                  filterCityPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <AddWinnerModal
        isOpen={isModalFormOpen}
        cityId={filterCityPage}
        onClose={() => handleCloseFormModal()}
      />
    </Box>
  );
};

export default RaffleWinnersContent;
